import { useState, useEffect, useRef } from 'react'
import { useGameStore, ENEMIES_FOR_STORE } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import { SecondChanceModal } from '@components/SecondChanceModal'
import { AdModal } from '@components/AdModal'
import './BattleScreen.css'

// Generate stage enemy using store definition


export function BattleScreen() {
    const battleStage = useGameStore(state => state.battleStage)
    const setBattleStage = useGameStore(state => state.setBattleStage)
    const maxStageReached = useGameStore(state => state.maxStageReached)
    const battleStageWins = useGameStore(state => state.battleStageWins)
    const incrementStageWins = useGameStore(state => state.incrementStageWins)
    const addXP = useGameStore(state => state.addXP)
    const autoProgress = useGameStore(state => state.autoProgress)
    const setAutoProgress = useGameStore(state => state.setAutoProgress)
    const isVIP = useGameStore(state => state.isVIP)
    const stats = useGameStore(state => state.stats) // { atk, def, spd }
    const playerLevel = useGameStore(state => state.playerLevel)
    const equipment = useGameStore(state => state.equipment) || {}
    const resources = useGameStore(state => state.resources)
    const addGold = useGameStore(state => state.addGold)
    const useEnergy = useGameStore(state => state.useEnergy)
    const spendGems = useGameStore(state => state.spendGems)
    const trackAdWatched = useGameStore(state => state.trackAdWatched)
    const setScreen = useGameStore(state => state.setScreen)
    const heroSkin = useGameStore(state => state.heroSkin)
    const power = useGameStore(state => state.power)

    // Tutorial integration
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)

    const currentStage = battleStage
    const energyCost = 5

    // --- STATE DECLARATIONS ---
    const [isPowerBoosted, setIsPowerBoosted] = useState(false)
    const [isBattling, setIsBattling] = useState(false)
    const [battleResult, setBattleResult] = useState<'win' | 'lose' | null>(null)
    const [heroAnim, setHeroAnim] = useState<string>('')
    const [enemyAnim, setEnemyAnim] = useState<string>('')
    const [floatingTexts, setFloatingTexts] = useState<Array<{ id: number, text: string, type: 'damage' | 'crit', x: number, target: 'hero' | 'enemy' }>>([])
    const [showEnergyPopup, setShowEnergyPopup] = useState(false)
    const [showAdModal, setShowAdModal] = useState(false)

    // Refs
    const textIdCounter = useRef(0)
    const hasSeenEnergyOffer = useRef(false)
    const lastHeroAttack = useRef(0)
    const lastEnemyAttack = useRef(0)
    const battleStartTime = useRef(0)
    const requestRef = useRef<number | null>(null)
    const victoryProcessed = useRef(false)

    // Slider & Transitions
    const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [prevEnemy, setPrevEnemy] = useState<any>(null)
    const [prevStageNum, setPrevStageNum] = useState<number | null>(null)

    const nextStageUnlocked = maxStageReached > currentStage || (battleStageWins[currentStage] || 0) >= 5

    // --- HERO STATS CALCULATION ---
    const equipmentPower = Object.values(equipment).reduce(
        (sum, item) => sum + (item?.power || 0),
        0
    )

    // Combat Stats with Boost Check
    const boostMultiplier = isPowerBoosted ? 1.5 : 1.0

    const heroAtk = Math.floor((stats.atk + Math.floor(equipmentPower * 0.2)) * boostMultiplier)
    const heroDef = Math.floor((stats.def + Math.floor(equipmentPower * 0.1)) * boostMultiplier)
    const heroSpd = stats.spd

    const heroMaxHp = Math.floor((100 + (playerLevel * 10) + (heroDef * 5)) * boostMultiplier)

    // Display Power
    // Display Power (Matched with Header)
    const statPower = (stats.atk + stats.def + stats.spd) * 5
    const baseTotalPower = power + equipmentPower + statPower
    const totalPower = Math.floor(baseTotalPower * boostMultiplier)
    const boostGain = Math.floor(baseTotalPower * 0.5)

    // Local State dependant on calculations
    const [heroHp, setHeroHp] = useState(heroMaxHp)

    // --- ENEMY GENERATION ---
    const generateEnemyData = (stage: number) => {
        const enemyIndex = Math.min(stage - 1, ENEMIES_FOR_STORE.length - 1)
        const baseEnemy = ENEMIES_FOR_STORE[enemyIndex]
        const scaling = 1 + (stage * 0.1)

        // Safety for missing base stats
        const bHp = (baseEnemy as any).baseHp || 100
        const bAtk = (baseEnemy as any).baseAtk || 10
        const bDef = (baseEnemy as any).baseDef || 0
        const bSpd = (baseEnemy as any).baseSpd || 5

        return {
            ...baseEnemy,
            power: Math.floor(baseEnemy.basePower * scaling),
            goldReward: Math.floor(baseEnemy.goldReward * scaling),
            expReward: Math.floor(baseEnemy.expReward),
            maxHp: Math.floor(bHp * scaling),
            atk: Math.floor(bAtk * scaling),
            def: Math.floor(bDef * scaling),
            spd: Math.floor(bSpd * scaling),
        }
    }

    const [currentEnemy, setCurrentEnemy] = useState(generateEnemyData(battleStage))
    const [enemyHp, setEnemyHp] = useState(currentEnemy.maxHp)

    // --- EFFECTS ---

    // Disable auto-progress if VIP expires OR energy is out
    useEffect(() => {
        if (autoProgress) {
            if (!isVIP) {
                setAutoProgress(false)
            } else if (resources.energy < energyCost) {
                setAutoProgress(false)
            }
        }
    }, [isVIP, autoProgress, resources.energy, energyCost])

    // Reset boost on stage change
    useEffect(() => {
        setIsPowerBoosted(false)
    }, [battleStage])

    // Reset boosters after battle ends
    useEffect(() => {
        if (battleResult && isPowerBoosted) {
            const timer = setTimeout(() => setIsPowerBoosted(false), 2000)
            return () => clearTimeout(timer)
        }
    }, [battleResult])

    // Hero Image Map
    const HERO_IMAGES: { [key: string]: string } = {
        'default': '/rookie.png',
        'knight': '/ritter.png',
        'king': '/king.png',
        'wizard': '/magier.png'
    }

    // Helper: Add Floating Text
    const addFloatingText = (text: string, type: 'damage' | 'crit', target: 'hero' | 'enemy') => {
        const id = textIdCounter.current++
        setFloatingTexts(prev => [...prev, { id, text, type, x: 50 + (Math.random() * 20 - 10), target }])
        // Cleanup after animation
        setTimeout(() => {
            setFloatingTexts(prev => prev.filter(t => t.id !== id))
        }, 1200)
    }

    // Helper: Trigger Animation
    const triggerAnim = (target: 'hero' | 'enemy', type: 'attack' | 'damage' | 'dead') => {
        if (target === 'hero') {
            if (type === 'attack') {
                setHeroAnim('battle--hit-hero')
                setTimeout(() => setHeroAnim(''), 400)
            } else if (type === 'damage') {
                setHeroAnim('battle--damage-hero')
                setTimeout(() => setHeroAnim(''), 400)
            }
        } else {
            if (type === 'attack') {
                setEnemyAnim('battle--hit-enemy')
                setTimeout(() => setEnemyAnim(''), 400)
            } else if (type === 'damage') {
                setEnemyAnim('battle--damage-enemy')
                setTimeout(() => setEnemyAnim(''), 400)
            } else if (type === 'dead') {
                setEnemyAnim('battle--dead')
            }
        }
    }

    // Check for low energy to trigger popup
    useEffect(() => {
        if (resources.energy < energyCost && !hasSeenEnergyOffer.current) {
            hasSeenEnergyOffer.current = true
            setShowEnergyPopup(true)
        }
    }, [resources.energy])

    // Update enemy when stage changes
    useEffect(() => {
        const newEnemy = generateEnemyData(currentStage)
        setCurrentEnemy(newEnemy)
        setEnemyHp(newEnemy.maxHp)
        setHeroHp(heroMaxHp)
        setBattleResult(null)
        setIsBattling(false)
        setEnemyAnim('') // Reset death anim
        setHeroAnim('')
        setFloatingTexts([])
    }, [currentStage, stats, equipment, playerLevel])

    const dealDamageToEnemy = (now: number) => {
        const rawDmg = heroAtk
        const mitigation = 100 / (100 + currentEnemy.def)
        const actualDmg = Math.max(1, Math.floor(rawDmg * mitigation))

        triggerAnim('hero', 'attack')
        setTimeout(() => triggerAnim('enemy', 'damage'), 150)
        setTimeout(() => addFloatingText(`-${actualDmg}`, 'damage', 'enemy'), 150)

        setEnemyHp(prev => {
            const next = prev - actualDmg
            if (next <= 0) return 0
            return next
        })
        lastHeroAttack.current = now
    }

    const dealDamageToHero = (now: number) => {
        const rawDmg = currentEnemy.atk
        const mitigation = 100 / (100 + heroDef)
        const actualDmg = Math.max(1, Math.floor(rawDmg * mitigation))

        triggerAnim('enemy', 'attack')
        setTimeout(() => triggerAnim('hero', 'damage'), 150)
        setTimeout(() => addFloatingText(`-${actualDmg}`, 'damage', 'hero'), 150)

        setHeroHp(prev => {
            const next = prev - actualDmg
            if (next <= 0) return 0
            return next
        })
        lastEnemyAttack.current = now
    }

    const handleGemWin = () => {
        if (!spendGems(500)) return

        // Start battle to ensure logic flows, then kill
        victoryProcessed.current = false // Reset ref to ensure win counts
        setIsBattling(true)
        setHeroHp(heroMaxHp) // Ensure hero is alive
        setEnemyHp(currentEnemy.maxHp)

        // Visual flair
        setTimeout(() => triggerAnim('hero', 'attack'), 50)
        setTimeout(() => {
            addFloatingText("-999999", 'crit', 'enemy')
            triggerAnim('enemy', 'damage')
            setEnemyHp(0) // This will trigger the useEffect win loop
        }, 300)
    }

    const handleAdBoost = () => {
        setShowAdModal(true)
    }

    const handleAdComplete = () => {
        setIsPowerBoosted(true)
        setShowAdModal(false)
        // Heal to max (new boosted maxHP)
        setTimeout(() => {
            // Recalculate max HP with boost is tricky since state update isn't instant
            // But next render with isPowerBoosted=true will update calculations
            // We force a start
            startBattle()
        }, 100)
    }

    // Start battle logic
    const startBattle = () => {
        if (isBattling) return
        if (resources.energy < energyCost) return
        if (!useEnergy(energyCost)) return

        setIsBattling(true)
        setBattleResult(null)
        setHeroHp(heroMaxHp)
        setEnemyHp(currentEnemy.maxHp)
        setEnemyAnim('')
        setHeroAnim('')
        victoryProcessed.current = false // Reset victory guard

        // Reset Timers
        const now = Date.now()
        battleStartTime.current = now
        lastHeroAttack.current = now
        lastEnemyAttack.current = 0

        dealDamageToEnemy(now)
    }

    // Auto-battle trigger
    useEffect(() => {
        if (autoProgress && !isBattling && !battleResult) {
            const timer = setTimeout(() => {
                startBattle()
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [autoProgress, isBattling, battleResult])

    // Clear result for auto-battle
    useEffect(() => {
        if (autoProgress && battleResult) {
            const timer = setTimeout(() => {
                setBattleResult(null)
                setHeroHp(heroMaxHp)
                setEnemyHp(currentEnemy.maxHp)
                setEnemyAnim('')
                setHeroAnim('')
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [autoProgress, battleResult, heroMaxHp, currentEnemy])

    useEffect(() => {
        if (!isBattling) return

        const loop = () => {
            const now = Date.now()
            const timeSinceStart = now - battleStartTime.current

            // Check Win/Loss
            if (enemyHp <= 0) {
                // Guard against multiple triggering
                if (victoryProcessed.current) {
                    if (requestRef.current) cancelAnimationFrame(requestRef.current)
                    return
                }
                victoryProcessed.current = true

                // Death Animation Trigger before Ending
                if (enemyAnim !== 'battle--dead') {
                    triggerAnim('enemy', 'dead')
                }

                // Simple Timeout wrapper for win state to let animation play
                setTimeout(() => {
                    setIsBattling(false)
                    setBattleResult('win')
                    addGold(currentEnemy.goldReward)
                    addXP(currentEnemy.expReward)
                    incrementStageWins(currentStage)

                    // Tutorial: Advance to daily phase after first victory
                    if (tutorialPhase === 'battle') {
                        setTimeout(() => {
                            setTutorialPhase('daily')
                            setScreen('daily')
                        }, 2000) // Wait for victory animation
                    }
                }, 500)

                // Cancel loop
                if (requestRef.current) cancelAnimationFrame(requestRef.current)
                return
            }
            // ... (Loss check and rest of loop)
            if (heroHp <= 0) {
                setIsBattling(false)
                setBattleResult('lose')
                setAutoProgress(false)
                return
            }

            // ... (Rest of loop)
            // Hero Status
            const heroInterval = Math.max(200, 30000 / Math.max(1, heroSpd))
            if (now - lastHeroAttack.current >= heroInterval) {
                dealDamageToEnemy(now)
            }

            // Enemy Status
            if (lastEnemyAttack.current === 0) {
                if (timeSinceStart >= 1000) {
                    dealDamageToHero(now)
                }
            } else {
                if (now - lastEnemyAttack.current >= 3000) {
                    dealDamageToHero(now)
                }
            }

            requestRef.current = requestAnimationFrame(loop)
        }

        requestRef.current = requestAnimationFrame(loop)
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current)
        }
    }, [isBattling, enemyHp, heroHp, heroAtk, heroSpd, heroDef, currentEnemy])

    // --- HELPERS ---


    const getBgUrl = (stage: number) => {
        if (stage >= 1 && stage <= 5) return `/kampf/bg-stufe${stage}.jpg`
        return '/bg-fight.jpg'
    }

    const prevStage = () => {
        if (currentStage > 1 && !isTransitioning) {
            setSlideDirection('right')
            setIsTransitioning(true)
            setPrevStageNum(currentStage)
            setPrevEnemy(currentEnemy)

            const nextS = currentStage - 1
            setBattleStage(nextS)
            // Logic handled by useEffect on [currentStage]
            setTimeout(() => {
                setIsTransitioning(false)
                setSlideDirection(null)
            }, 400)
        }
    }

    const nextStage = () => {
        if (nextStageUnlocked && !isTransitioning) {
            setSlideDirection('left')
            setIsTransitioning(true)
            setPrevStageNum(currentStage)
            setPrevEnemy(currentEnemy)

            const nextS = currentStage + 1
            setBattleStage(nextS)

            setTimeout(() => {
                setIsTransitioning(false)
                setSlideDirection(null)
            }, 400)
        }
    }

    const handleRetry = () => {
        setBattleResult(null)
        setHeroHp(heroMaxHp)
        setEnemyHp(currentEnemy.maxHp)
        setEnemyAnim('')
        setHeroAnim('')
        setFloatingTexts([])
    }

    return (
        <GameLayout noPadding noScroll showHeader={true} showNav={true}>
            <div className="battle">
                {/* STAGE SLIDER WRAPPER */}
                <div className="battle__stage-wrapper">
                    {/* EXITING STAGE (Visual Only) */}
                    {isTransitioning && prevEnemy && prevStageNum !== null && (
                        <div className={`battle__stage-container ${slideDirection === 'left' ? 'slide-out-left' : 'slide-out-right'}`}>
                            <div className="battle__bg" style={{ backgroundImage: `url('${getBgUrl(prevStageNum)}')` }} />
                            <div className="battle__arena">
                                {/* Simplified view for transition */}
                            </div>
                        </div>
                    )}

                    {/* ENTERING / ACTIVE STAGE */}
                    <div className={`battle__stage-container ${isTransitioning ? (slideDirection === 'left' ? 'slide-in-left' : 'slide-in-right') : ''}`}>
                        <div className="battle__bg" style={{ backgroundImage: `url('${getBgUrl(currentStage)}')` }} />
                        <div className="battle__arena">

                            {/* HERO SIDE */}
                            <div className={`battle__side battle__side--hero`}>
                                <div className="battle__char-wrap">
                                    {/* HP BAR */}
                                    <div className="battle__hp-bar">
                                        <div className="battle__hp-fill" style={{ width: `${(heroHp / heroMaxHp) * 100}%`, background: '#22c55e' }}></div>
                                    </div>

                                    {/* FLOATING TEXT HERO */}
                                    {floatingTexts.filter(t => t.target === 'hero').map(ft => (
                                        <div key={ft.id} className={`battle__float-text ${ft.type === 'crit' ? 'battle__float-text--crit' : ''}`} style={{ left: `${ft.x}%` }}>
                                            {ft.text}
                                        </div>
                                    ))}

                                    <img src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default} alt="Held" className={`battle__hero-img ${heroAnim}`} />
                                    <div className="battle__power-badge battle__power-badge--hero">⚔️ {totalPower}</div>
                                </div>
                            </div>

                            {/* VS DIVIDER */}
                            <div className="battle__vs-container">
                                <div className={`battle__vs-text ${isBattling ? 'battle__vs-text--anim' : ''}`}>VS</div>
                            </div>



                            {/* ENERGY POPUP */}
                            <div className={`battle__side battle__side--enemy`}>
                                <div className="battle__char-wrap">
                                    {/* HP BAR */}
                                    {/* Allow HP bar to be empty if dead */}
                                    <div className="battle__hp-bar">
                                        <div className="battle__hp-fill" style={{ width: `${(enemyHp / currentEnemy.maxHp) * 100}%`, background: '#ef4444' }}></div>
                                    </div>

                                    {/* FLOATING TEXT ENEMY */}
                                    {floatingTexts.filter(t => t.target === 'enemy').map(ft => (
                                        <div key={ft.id} className={`battle__float-text ${ft.type === 'crit' ? 'battle__float-text--crit' : ''}`} style={{ left: `${ft.x}%` }}>
                                            {ft.text}
                                        </div>
                                    ))}

                                    <div className={`battle__enemy-emoji ${enemyAnim}`} style={{ opacity: enemyHp === 0 && enemyAnim !== 'battle--dead' ? 0 : 1 }}>{currentEnemy.emoji}</div>
                                    <div className="battle__power-badge battle__power-badge--enemy">⚔️ {currentEnemy.power}</div>
                                </div>
                            </div>

                            {/* FLOATING STATUS MESSAGE */}
                            {(isBattling || battleResult) && (
                                <div className="battle__status battle__status--floating">
                                    {battleResult === 'win' && (
                                        <div className="battle__status-msg battle__status-msg--win">
                                            <div className="battle__win-line">🏆 SIEG! <br /> 🪙{currentEnemy.goldReward} &nbsp; ✨ {currentEnemy.expReward} XP</div>
                                            {(battleStageWins[currentStage] || 0) === 5 && maxStageReached === currentStage + 1 && (
                                                <div className="battle__unlock-msg">🔓 NÄCHSTE STUFE FREIGESCHALET!</div>
                                            )}
                                        </div>
                                    )}
                                    {battleResult === 'lose' && (
                                        <div className="battle__status-msg battle__status-msg--lose">💀 NIEDERLAGE!</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Energy Empty Popup */}
                <SecondChanceModal
                    trigger="energy_empty"
                    isOpen={showEnergyPopup}
                    onClose={() => setShowEnergyPopup(false)}
                />

                {showAdModal && (
                    <AdModal
                        onComplete={handleAdComplete}
                        onCancel={() => setShowAdModal(false)}
                    />
                )}

                <div className="battle__content">
                    {/* TOP HEADER: UNIFIED STAGE & REWARD BOX */}
                    <div className="battle__header-unified">
                        <button
                            className={`battle__nav-btn ${currentStage <= 1 ? 'battle__nav-btn--disabled' : ''}`}
                            onClick={prevStage}
                            disabled={currentStage <= 1}
                        >
                            ◀
                        </button>

                        <div className="battle__header-divider" />

                        <div className="battle__header-info">
                            <div className="battle__info-group">
                                <span className="battle__info-lbl">STUFE</span>
                                <span className="battle__info-val">{currentStage}</span>
                            </div>

                            <div className="battle__header-v-divider" />

                            <div className="battle__info-group">
                                <span className="battle__info-lbl">BELOHNUNG</span>
                                <div className="battle__info-val battle__info-val--multi">
                                    <span>🪙 {currentEnemy.goldReward >= 1000 ? (currentEnemy.goldReward / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'K' : currentEnemy.goldReward}</span>
                                    <span>✨ {currentEnemy.expReward >= 1000 ? (currentEnemy.expReward / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'K' : currentEnemy.expReward} XP</span>
                                </div>
                            </div>
                        </div>

                        <div className="battle__header-divider" />

                        <button
                            className={`battle__nav-btn ${!nextStageUnlocked ? 'battle__nav-btn--locked' : ''}`}
                            onClick={nextStage}
                            disabled={!nextStageUnlocked}
                        >
                            {!nextStageUnlocked ? (
                                <div className="battle__lock-wrap">
                                    <span>🔒</span>
                                    <small>{battleStageWins[currentStage] || 0}/5</small>
                                </div>
                            ) : '▶'}
                        </button>
                    </div>

                    {/* ACTIONS (GLASSMORPHISM PANEL) */}
                    <div className="battle__bottom-panel">



                        {/* Action Buttons */}
                        <div className="battle__actions">
                            {battleResult !== 'lose' && (
                                <>
                                    <button
                                        id="battle-attack-btn"
                                        className="battle__btn battle__btn--fight"
                                        onClick={startBattle}
                                        disabled={isBattling || resources.energy < energyCost}
                                    >
                                        <div className="battle__btn-content">
                                            <div className="battle__btn-left">
                                                <span className="battle__btn-icon">⚔️</span>
                                                <span className="battle__btn-txt">{isBattling ? 'KÄMPFT...' : 'KÄMPFEN'}</span>
                                            </div>
                                            <div className="battle__btn-divider" />
                                            <div className="battle__btn-right">
                                                {energyCost} ⚡
                                            </div>
                                        </div>
                                    </button>

                                    {/* Action Buttons (Only if weaker) */}
                                    {!isBattling && !battleResult && totalPower < currentEnemy.power && (
                                        <div className="battle__emergency-btns">
                                            <button className="battle__btn-sm battle__btn-sm--ad" onClick={handleAdBoost}>
                                                <div className="battle__btn-content">
                                                    <div className="battle__btn-left">
                                                        <span>+{boostGain} ⚔️</span>
                                                    </div>
                                                    <div className="battle__btn-divider" />
                                                    <div className="battle__btn-right">
                                                        <span>ADS</span>
                                                    </div>
                                                </div>
                                            </button>
                                            <button className="battle__btn-sm battle__btn-sm--gem" onClick={handleGemWin}>
                                                <div className="battle__btn-content">
                                                    <div className="battle__btn-left">
                                                        <span>WIN</span>
                                                    </div>
                                                    <div className="battle__btn-divider" />
                                                    <div className="battle__btn-right">
                                                        <span>💎 500</span>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {battleResult === 'lose' && (
                                <div className="battle__defeat-actions">
                                    <button className="battle__btn battle__btn--retry w-full" onClick={handleRetry}>
                                        <div className="battle__btn-content">
                                            <div className="battle__btn-left">NOCHMAL</div>
                                            <div className="battle__btn-divider" />
                                            <div className="battle__btn-right">
                                                0 ⚡
                                            </div>
                                        </div>
                                    </button>
                                    <button className="battle__btn-sm battle__btn-sm--back w-full" onClick={() => setScreen('hero')}>
                                        🦸 HELD VERSTÄRKEN
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* AUTO-BATTLE TOGGLE */}
                        <div className="battle__auto-panel">
                            {isVIP ? (
                                <label className="battle__auto-toggle">
                                    <input type="checkbox" checked={autoProgress} onChange={(e) => setAutoProgress(e.target.checked)} />
                                    <span className="battle__slider"></span>
                                    <span className="battle__toggle-lbl">AUTO-KAMPF AKTIV</span>
                                </label>
                            ) : (
                                <button className="battle__vip-unlock" onClick={() => setScreen('shop')}>
                                    👑 VIP FÜR AUTO-KAMPF
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </GameLayout>
    )
}
